import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  useTheme,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  ShoppingBag,
  People,
  ShoppingCart,
  Message
} from '@mui/icons-material';
import { THEME_COLORS, GRADIENTS, SHADOWS } from '../../theme/themeConfig';

const Dashboard: React.FC = () => {
  const theme = useTheme();

  const statsCards = [
    {
      title: 'Weekly sales',
      value: '714k',
      change: '+2.6%',
      trend: 'up',
      color: THEME_COLORS.accent.blue,
      bgColor: GRADIENTS.blue,
      icon: <ShoppingBag sx={{ fontSize: 40, color: THEME_COLORS.accent.blue }} />
    },
    {
      title: 'New users',
      value: '1.35m',
      change: '-0.1%',
      trend: 'down',
      color: THEME_COLORS.accent.purple,
      bgColor: GRADIENTS.purple,
      icon: <People sx={{ fontSize: 40, color: THEME_COLORS.accent.purple }} />
    },
    {
      title: 'Purchase orders',
      value: '1.72m',
      change: '+2.8%',
      trend: 'up',
      color: THEME_COLORS.accent.orange,
      bgColor: GRADIENTS.orange,
      icon: <ShoppingCart sx={{ fontSize: 40, color: THEME_COLORS.accent.orange }} />
    },
    {
      title: 'Messages',
      value: '234',
      change: '+3.6%',
      trend: 'up',
      color: THEME_COLORS.accent.red,
      bgColor: GRADIENTS.red,
      icon: <Message sx={{ fontSize: 40, color: THEME_COLORS.accent.red }} />
    }
  ];

  const StatCard = ({ stat }: { stat: any }) => (
    <Card sx={{ 
      height: '100%',
      borderRadius: 3,
      border: 'none',
      boxShadow: SHADOWS.md,
      position: 'relative',
      overflow: 'hidden',
      '&:hover': {
        boxShadow: SHADOWS.lg,
        transform: 'translateY(-2px)',
      }
    }}>
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: stat.bgColor,
        opacity: 0.5
      }} />
      <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="body2" sx={{ 
              color: THEME_COLORS.neutral.gray[500], 
              fontWeight: 500,
              mb: 1
            }}>
              {stat.title}
            </Typography>
            <Typography variant="h3" sx={{ 
              fontWeight: 'bold',
              color: THEME_COLORS.neutral.gray[800],
              mb: 1,
              fontSize: '2.5rem'
            }}>
              {stat.value}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {stat.trend === 'up' ? (
                <TrendingUp sx={{ color: THEME_COLORS.status.success, fontSize: 18 }} />
              ) : (
                <TrendingDown sx={{ color: THEME_COLORS.status.error, fontSize: 18 }} />
              )}
              <Typography variant="body2" sx={{ 
                color: stat.trend === 'up' ? THEME_COLORS.status.success : THEME_COLORS.status.error,
                fontWeight: 600
              }}>
                {stat.change}
              </Typography>
            </Box>
          </Box>
          <Box sx={{
            width: 60,
            height: 60,
            borderRadius: 2,
            backgroundColor: stat.bgColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {stat.icon}
          </Box>
        </Box>
        
        {/* Mini chart area */}
        <Box sx={{ height: 40, mt: 2, position: 'relative' }}>
          <svg width="100%" height="100%" viewBox="0 0 200 40">
            <defs>
              <linearGradient id={`gradient-${stat.color}`} x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" style={{ stopColor: stat.color, stopOpacity: 0.3 }} />
                <stop offset="100%" style={{ stopColor: stat.color, stopOpacity: 0.1 }} />
              </linearGradient>
            </defs>
            {stat.trend === 'up' ? (
              <path
                d="M0,30 Q20,25 40,20 T80,15 T120,10 T160,8 T200,5"
                stroke={stat.color}
                strokeWidth="2"
                fill={`url(#gradient-${stat.color})`}
                fillRule="evenodd"
              />
            ) : (
              <path
                d="M0,10 Q20,15 40,20 T80,25 T120,30 T160,32 T200,35"
                stroke={stat.color}
                strokeWidth="2"
                fill={`url(#gradient-${stat.color})`}
                fillRule="evenodd"
              />
            )}
          </svg>
        </Box>
      </CardContent>
    </Card>
  );

  const PieChart = () => (
    <svg width="200" height="200" viewBox="0 0 200 200">
      <circle
        cx="100"
        cy="100"
        r="80"
        fill="none"
        stroke={THEME_COLORS.neutral.gray[100]}
        strokeWidth="2"
      />
      {/* Blue section - 43.8% */}
      <path
        d="M 100,20 A 80,80 0 0,1 175.11,66.59 L 100,100 Z"
        fill={THEME_COLORS.accent.blue}
      />
      {/* Yellow section - 31.3% */}
      <path
        d="M 175.11,66.59 A 80,80 0 0,1 148.28,171.71 L 100,100 Z"
        fill={THEME_COLORS.accent.orange}
      />
      {/* Teal section - 18.8% */}
      <path
        d="M 148.28,171.71 A 80,80 0 0,1 51.72,171.71 L 100,100 Z"
        fill={THEME_COLORS.accent.teal}
      />
      {/* Red section - 6.3% */}
      <path
        d="M 51.72,171.71 A 80,80 0 0,1 100,20 L 100,100 Z"
        fill={THEME_COLORS.accent.red}
      />
    </svg>
  );

  const BarChart = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep'];
    const teamAData = [40, 30, 65, 45, 35, 65, 35, 20, 50];
    const teamBData = [50, 65, 45, 65, 38, 30, 25, 65, 25];
    
    return (
      <Box sx={{ width: '100%', height: 300, position: 'relative' }}>
        <svg width="100%" height="100%" viewBox="0 0 400 240">
          {/* Grid lines */}
          {[0, 20, 40, 60, 80].map((y) => (
            <line
              key={y}
              x1="40"
              y1={200 - y * 2}
              x2="380"
              y2={200 - y * 2}
              stroke={THEME_COLORS.neutral.gray[100]}
              strokeWidth="1"
            />
          ))}
          
          {/* Bars */}
          {months.map((month, index) => {
            const x = 50 + index * 35;
            const teamAHeight = teamAData[index] * 2;
            const teamBHeight = teamBData[index] * 2;
            
            return (
              <g key={month}>
                {/* Team A bars */}
                <rect
                  x={x}
                  y={200 - teamAHeight}
                  width="12"
                  height={teamAHeight}
                  fill={THEME_COLORS.primary.main}
                  rx="2"
                />
                {/* Team B bars */}
                <rect
                  x={x + 15}
                  y={200 - teamBHeight}
                  width="12"
                  height={teamBHeight}
                  fill={THEME_COLORS.accent.orange}
                  rx="2"
                />
                {/* Month labels */}
                <text
                  x={x + 13}
                  y="220"
                  fill={THEME_COLORS.neutral.gray[500]}
                  fontSize="10"
                  textAnchor="middle"
                >
                  {month}
                </text>
              </g>
            );
          })}
          
          {/* Y-axis labels */}
          {[0, 20, 40, 60, 80].map((y) => (
            <text
              key={y}
              x="30"
              y={205 - y * 2}
              fill={THEME_COLORS.neutral.gray[500]}
              fontSize="10"
              textAnchor="end"
            >
              {y}
            </text>
          ))}
        </svg>
        
        {/* Legend */}
        <Box sx={{ 
          position: 'absolute', 
          top: 16, 
          right: 16,
          display: 'flex',
          gap: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ 
              width: 12, 
              height: 12, 
              backgroundColor: THEME_COLORS.primary.main, 
              borderRadius: '50%' 
            }} />
            <Typography variant="caption" sx={{ color: THEME_COLORS.neutral.gray[500] }}>
              Team A
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ 
              width: 12, 
              height: 12, 
              backgroundColor: THEME_COLORS.accent.orange, 
              borderRadius: '50%' 
            }} />
            <Typography variant="caption" sx={{ color: THEME_COLORS.neutral.gray[500] }}>
              Team B
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statsCards.map((stat, index) => (
          <Grid item xs={12} sm={6} lg={3} key={index}>
            <StatCard stat={stat} />
          </Grid>
        ))}
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3}>
        {/* Current Visits - Pie Chart */}
        <Grid item xs={12} md={5}>
          <Card sx={{ 
            height: 400,
            borderRadius: 3,
            boxShadow: SHADOWS.md
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ 
                fontWeight: 'bold', 
                color: THEME_COLORS.neutral.gray[800],
                mb: 3
              }}>
                Current visits
              </Typography>
              
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                mb: 3
              }}>
                <PieChart />
              </Box>
              
              {/* Legend */}
              <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 2 }}>
                {[
                  { label: 'Desktop', color: THEME_COLORS.accent.blue, percentage: '43.8%' },
                  { label: 'Mobile', color: THEME_COLORS.accent.orange, percentage: '31.3%' },
                  { label: 'Tablet', color: THEME_COLORS.accent.teal, percentage: '18.8%' },
                  { label: 'Other', color: THEME_COLORS.accent.red, percentage: '6.3%' }
                ].map((item) => (
                  <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ 
                      width: 12, 
                      height: 12, 
                      backgroundColor: item.color, 
                      borderRadius: '50%' 
                    }} />
                    <Typography variant="caption" sx={{ color: THEME_COLORS.neutral.gray[500] }}>
                      {item.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Website Visits - Bar Chart */}
        <Grid item xs={12} md={7}>
          <Card sx={{ 
            height: 400,
            borderRadius: 3,
            boxShadow: SHADOWS.md
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography variant="h6" sx={{ 
                    fontWeight: 'bold', 
                    color: THEME_COLORS.neutral.gray[800]
                  }}>
                    Website visits
                  </Typography>
                  <Typography variant="body2" sx={{ color: THEME_COLORS.neutral.gray[500] }}>
                    (+43%) than last year
                  </Typography>
                </Box>
              </Box>
              
              <BarChart />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;